import { useDraggable } from '@dnd-kit/core';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { Theme, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { CSSProperties, useRef, useState } from 'react';

import JobStatusChip from '~components/Job/JobStatusChip';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { Job } from '~hooks/useJob/models';
import { useStores } from '~store';
import { dateFormat } from '~utils/dateTime';

import { FULL_HOUR_WIDTH, JOB_WIDTH, MIN_CARD_WIDTH } from './constants';
import JobCardActionButton, { JOB_CARD_INNER_GUTTER_SIZE } from './JobCardActionButton';

// Maximum possible job card width: a 24-hour job at 80px/hour = 1920px.
// Used to compute z-index inversely proportional to duration so shorter
// cards always render on top of longer ones.
const HOURS_IN_DAY = 24;
const MAX_JOB_CARD_WIDTH_PX = HOURS_IN_DAY * FULL_HOUR_WIDTH;
const HOVERED_JOB_CARD_Z_INDEX = 100;

export enum JobPlacement {
  DRAGGING = 'dragging',
  JOBS_COLUMN = 'jobs_column',
  DRIVERS_GRID = 'drivers_grid',
}

const getBackgroundColor = (jobPlacement: JobPlacement | undefined, theme: Theme) => {
  switch (jobPlacement) {
    case JobPlacement.DRAGGING:
      return theme.brandV2.colors.treadGray8;
    case JobPlacement.JOBS_COLUMN:
      return theme.semantic.surfaceRaised;
    default:
      return theme.brandV2.colors.treadGray10;
  }
};

interface DraggableJobProps {
  jobId: string;
  style?: CSSProperties;
  jobPlacement?: JobPlacement;
  columnSpan?: number;
  stackedJobsCount?: number;
  slotKey?: string;
}

export const JobCard = observer(
  ({
    jobId,
    style,
    jobPlacement,
    columnSpan = 1,
    stackedJobsCount = 1,
    slotKey,
  }: DraggableJobProps) => {
    const { driverSchedulerStore } = useStores();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isStackMenuOpen, setIsStackMenuOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const job =
      driverSchedulerStore.unassignedJobs.find((job) => job.id === jobId) ||
      driverSchedulerStore.assignedJobs.find((job) => job.id === jobId) ||
      driverSchedulerStore.draggedJob;
    // The backend reports an assigned job as non-assignable whatever the company, so this
    // only isolates entitlement while there is no assignee — reassignment stays draggable.
    const jobHasNoAssignee = !job?.driver && !job?.vendorJobAssignment?.vendorAccount;
    const actingCompanyCannotDispatchJob =
      jobHasNoAssignee && job?.driverOrVendorAssignable === false;

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: jobId,
      disabled: isMenuOpen || isStackMenuOpen || actingCompanyCannotDispatchJob,
    });

    if (!job) return null;

    let isShortCard = false;
    const cardMinWidth = JOB_WIDTH * columnSpan;
    let cardWidth = cardMinWidth;

    if (
      jobPlacement &&
      [JobPlacement.DRIVERS_GRID, JobPlacement.DRAGGING].includes(jobPlacement)
    ) {
      // For completed jobs, use actual work time from job summary if available
      // Otherwise, use forecasted job time
      const isJobCompleted = job.status === JobState.COMPLETED;
      const actualWorkTimeMinutes = job.jobSummary?.workTimeMinutes;
      const timeInMinutes =
        isJobCompleted && actualWorkTimeMinutes ? actualWorkTimeMinutes : job.jobTime;

      if (timeInMinutes) {
        const jobTimeInHours = timeInMinutes / 60;
        cardWidth = Math.max(MIN_CARD_WIDTH, jobTimeInHours * FULL_HOUR_WIDTH);
        isShortCard = cardWidth < cardMinWidth;
      }
    }

    const isDriverGrid = jobPlacement === JobPlacement.DRIVERS_GRID;

    const hoverWidth = isHovered && isShortCard ? cardMinWidth : cardWidth;

    const draggableStyle: CSSProperties = {
      ...style,
      width: `${hoverWidth}px`,
      ...(isDriverGrid && {
        transition: 'width 0.5s ease-out',
      }),
      zIndex: (() => {
        if (jobPlacement !== JobPlacement.DRIVERS_GRID) return 1;
        if (isHovered) return HOVERED_JOB_CARD_Z_INDEX;
        // Shorter-duration cards get a higher z-index so they render
        // on top of longer cards that may visually overlap them.
        return Math.min(
          HOVERED_JOB_CARD_Z_INDEX - 1,
          Math.max(1, Math.ceil(MAX_JOB_CARD_WIDTH_PX / Math.max(cardWidth, 1))),
        );
      })(),
      ...(jobPlacement === JobPlacement.JOBS_COLUMN && {
        left: '4px',
        transition: 'none',
      }),
      ...(transform && {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }),
    };

    if (isDragging && jobPlacement === JobPlacement.DRIVERS_GRID) {
      return null;
    }

    return (
      <Box
        ref={setNodeRef}
        style={draggableStyle}
        {...listeners}
        {...attributes}
        aria-label={'job-card'}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <JobCardContent
          job={job}
          jobPlacement={jobPlacement}
          onActionMenuOpenStateChanged={setIsMenuOpen}
          onStackMenuOpenStateChanged={setIsStackMenuOpen}
          slotKey={slotKey}
          stackedJobsCount={stackedJobsCount}
        />
      </Box>
    );
  },
);

interface JobCardContentProps {
  job: Job;
  jobPlacement?: JobPlacement;
  onActionMenuOpenStateChanged: (isOpen: boolean) => void;
  onStackMenuOpenStateChanged: (isOpen: boolean) => void;
  slotKey?: string;
  stackedJobsCount: number;
}

const JobCardContent = observer(
  ({
    job,
    jobPlacement,
    onActionMenuOpenStateChanged,
    onStackMenuOpenStateChanged,
    slotKey,
    stackedJobsCount,
  }: JobCardContentProps) => {
    const theme = useTheme();
    const { dropOffWayPoints, jobStartAt, material, pickUpWayPoints } = job;

    // The site name can be a full address, and in that case, we only want to grab the street name
    const pickUpSiteName = pickUpWayPoints?.[0]?.site?.name.split(',')[0] ?? '-';
    const dropOffSiteName = dropOffWayPoints?.[0]?.site?.name.split(',')[0] ?? '-';

    const jobStartTime = jobStartAt ? dateFormat(jobStartAt, 'hh:mm A') : '';
    const materialName = material?.name;

    return (
      <Box
        sx={{
          p: JOB_CARD_INNER_GUTTER_SIZE,
          borderRadius: '12px',
          background: getBackgroundColor(jobPlacement, theme),
          boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.20)',
          position: 'relative',
          transition: 'background 0.2s',
          ...(jobPlacement === JobPlacement.DRAGGING && {
            boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
          }),
          ...(jobPlacement === JobPlacement.JOBS_COLUMN && {
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          }),
          ...(jobPlacement === JobPlacement.DRIVERS_GRID && {
            pt: 0.75,
            pb: 0.75,
            borderRadius: '0 12px 12px 0',
            borderLeft: `4px solid ${theme.brandV2.colors.treadGray4}`,
            boxShadow: '2px 2px 8px 0px rgba(0, 0, 0, 0.30)',
          }),
        }}
      >
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: '1fr auto',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.3,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <Tooltip title={pickUpWayPoints?.[0]?.site?.name ?? pickUpSiteName}>
                  <Typography
                    component="span"
                    sx={{
                      color: theme.brandV2.colors.treadBlack,
                      fontSize: '12px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pickUpSiteName}
                  </Typography>
                </Tooltip>

                <Typography
                  component="span"
                  sx={{ color: theme.brandV2.colors.treadGray3, fontSize: '12px' }}
                >
                  {`\u00A0${t('common.to')}`}
                </Typography>
              </Box>

              <Tooltip title={dropOffWayPoints?.[0]?.site?.name ?? dropOffSiteName}>
                <Typography
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    fontSize: '12px',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dropOffSiteName}
                </Typography>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <JobStatusChip jobState={job.status} hideDropdownArrow />

              {stackedJobsCount > 1 && slotKey ? (
                <StackedJobsMenu
                  slotKey={slotKey}
                  stackedJobsCount={stackedJobsCount}
                  onOpenStateChanged={onStackMenuOpenStateChanged}
                />
              ) : (
                <StackedJobsCountBadge jobsCount={stackedJobsCount} />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              mt: 0.5,
              overflow: 'hidden',
              width: 'calc(100% - 32px)',
            }}
          >
            <Typography
              sx={{
                color: theme.brandV2.colors.treadGray2,
                flexShrink: 0,
                fontSize: '12px',
              }}
            >
              {jobStartTime}
            </Typography>

            {materialName && (
              <Typography sx={{ ml: 0.5, mr: 0.5, flexShrink: 0, fontSize: '12px' }}>
                •
              </Typography>
            )}

            <Box
              component="span"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Tooltip title={materialName}>
                <Typography
                  sx={{
                    color: theme.brandV2.colors.treadGray2,
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {materialName}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          <JobCardActionButton
            job={job}
            onMenuOpenedStateChange={onActionMenuOpenStateChanged}
          />
        </Box>
      </Box>
    );
  },
);

const STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX = 18;

function StackedJobsCountBadge({ jobsCount }: { jobsCount: number }) {
  if (jobsCount <= 1) {
    return null;
  }

  const stackedJobsCountText = jobsCount > 99 ? '99+' : jobsCount;

  return (
    <Box
      sx={{
        bgcolor: 'text.primary',
        borderRadius: '50%',
        color: 'background.paper',
        fontSize: '10px',
        fontWeight: 600,
        height: `${STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX}px`,
        lineHeight: `${STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX}px`,
        textAlign: 'center',
        width: `${STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX}px`,
      }}
    >
      {stackedJobsCountText}
    </Box>
  );
}

interface StackedJobsMenuProps {
  slotKey: string;
  stackedJobsCount: number;
  onOpenStateChanged: (isOpen: boolean) => void;
}

const StackedJobsMenu = observer(
  ({ slotKey, stackedJobsCount, onOpenStateChanged }: StackedJobsMenuProps) => {
    const theme = useTheme();
    const { driverSchedulerStore } = useStores();
    const menuHandler = useRef<MenuHandler>(null);
    const stackedJobs = driverSchedulerStore.jobStacks[slotKey] ?? [];
    const badgeText = stackedJobsCount > 99 ? '99+' : stackedJobsCount;

    return (
      <Menu
        ref={menuHandler}
        onOpenStateChanged={onOpenStateChanged}
        menuTrigger={
          <Box
            data-no-dnd="true"
            sx={{
              bgcolor: 'text.primary',
              borderRadius: '50%',
              color: 'background.paper',
              cursor: 'pointer',
              fontSize: '10px',
              fontWeight: 600,
              height: `${STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX}px`,
              lineHeight: `${STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX}px`,
              textAlign: 'center',
              width: `${STACKED_JOBS_COUNT_BADGE_SIZE_IN_PX}px`,
            }}
          >
            {badgeText}
          </Box>
        }
      >
        {stackedJobs.map((stackedJob) => {
          // Look up the latest job data from the store so the menu
          // reflects real-time updates without requiring a page reload.
          const latestJob =
            driverSchedulerStore.assignedJobs.find((j) => j.id === stackedJob.id) ||
            driverSchedulerStore.unassignedJobs.find((j) => j.id === stackedJob.id) ||
            stackedJob;

          const pickUp = latestJob.pickUpWayPoints?.[0]?.site?.name?.split(',')[0] ?? '-';
          const dropOff =
            latestJob.dropOffWayPoints?.[0]?.site?.name?.split(',')[0] ?? '-';

          return (
            <MenuItem
              key={stackedJob.id}
              onClick={(event) => {
                event.stopPropagation();
                driverSchedulerStore.promoteJobInStack(slotKey, stackedJob.id);
                menuHandler.current?.onClose?.();
              }}
              sx={{ py: 0.75, minHeight: 'auto' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    noWrap
                    color={theme.brandV2.colors.treadBlack}
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {pickUp} {t('common.to')} {dropOff}
                  </Typography>

                  <Typography
                    noWrap
                    color={theme.brandV2.colors.treadGray3}
                    sx={{
                      fontSize: '11px',
                    }}
                  >
                    {latestJob.jobId}
                  </Typography>
                </Box>

                <JobStatusChip jobState={latestJob.status} hideDropdownArrow />
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    );
  },
);
